/**
 * CUSTOM VALIDATION MODELS
 *
 * The core 10-dimension battery is epistemology — immutable, always runs.
 * This module is the customer's lens — their own domain-specific tests
 * layered on top.
 *
 * Product managers name their tests. Define their states.
 * Manually curate assignments per cluster.
 * Record their reasoning.
 *
 * No code. No formulas. States only.
 * The customer IS the validation engine for their custom layer.
 *
 * Persists in localStorage. One model per field instance.
 */

import type { StateClass } from './validation';

// ─── TYPES ──────────────────────────────────────────────────────

/** A categorical state within a custom dimension */
export interface CustomState {
  id: string;          // kebab-case identifier, e.g. 'aligned'
  label: string;       // display name, e.g. 'Aligned'
  stateClass: StateClass;  // maps to strong / forming / weak
}

/** A single custom validation dimension */
export interface CustomDimension {
  id: string;          // unique identifier
  name: string;        // e.g., "Brand Alignment"
  description: string; // what this test measures
  states: CustomState[];  // 2–4 categorical states (ordered)
  assignments: Record<string, CustomAssignment>;  // clusterId → assignment
}

/** Per-cluster assignment within a custom dimension */
export interface CustomAssignment {
  stateId: string;     // which state is assigned
  notes: string;       // customer's reasoning
}

/** The complete custom validation model */
export interface CustomValidationModel {
  version: number;               // schema version (1)
  dimensions: CustomDimension[];
  createdAt: number;
  updatedAt: number;
}

// ─── CONSTANTS ──────────────────────────────────────────────────

const STORAGE_KEY = 'aura-custom-validation';
const SCHEMA_VERSION = 1;

// ─── ID GENERATION ──────────────────────────────────────────────

/** Generate a short unique identifier */
export function generateId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).substring(2, 8)}`;
}

/** Generate a state ID from a label (kebab-case) */
export function stateIdFromLabel(label: string): string {
  return label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ─── CRUD OPERATIONS ────────────────────────────────────────────

/** Load the custom validation model from localStorage */
export function loadCustomModel(): CustomValidationModel {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as CustomValidationModel;
      if (parsed.version === SCHEMA_VERSION) {
        return parsed;
      }
      // Future: migration logic for version upgrades
    }
  } catch (e) {
    console.warn('Failed to load custom validation model:', e);
  }

  // Return empty model
  return createEmptyModel();
}

const GATEWAY = import.meta.env.VITE_API_URL || '';

/** Save the custom validation model — localStorage (instant) + backend (persistent) */
export function saveCustomModel(model: CustomValidationModel): void {
  model.updatedAt = Date.now();
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(model));
  } catch (e) {
    console.warn('Failed to save custom validation model:', e);
  }

  // Sync to backend — fire and forget, keyed per-dimension per-cluster
  if (GATEWAY) {
    for (const dim of model.dimensions) {
      for (const [clusterId, assignment] of Object.entries(dim.assignments)) {
        fetch(`${GATEWAY}/api/validations/${clusterId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dimensions: { [dim.id]: { state: assignment.stateId, evidence: [assignment.notes] } },
            overallState: 'in_progress',
          }),
        }).catch(() => {});
      }
    }
  }
}

/** Create a fresh empty model */
export function createEmptyModel(): CustomValidationModel {
  return {
    version: SCHEMA_VERSION,
    dimensions: [],
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
}

/** Create a new dimension from builder input */
export function createDimension(
  name: string,
  description: string,
  states: CustomState[],
): CustomDimension {
  return {
    id: generateId(),
    name: name.trim(),
    description: description.trim(),
    states,
    assignments: {},
  };
}

/** Add a dimension to the model */
export function addDimension(
  model: CustomValidationModel,
  dimension: CustomDimension,
): void {
  model.dimensions.push(dimension);
  saveCustomModel(model);
}

/** Remove a dimension from the model */
export function removeDimension(
  model: CustomValidationModel,
  dimensionId: string,
): void {
  model.dimensions = model.dimensions.filter(d => d.id !== dimensionId);
  saveCustomModel(model);
}

/** Set the state assignment for a cluster within a dimension */
export function setAssignment(
  model: CustomValidationModel,
  dimensionId: string,
  clusterId: string,
  stateId: string,
  notes?: string,
): void {
  const dim = model.dimensions.find(d => d.id === dimensionId);
  if (!dim) return;

  const existing = dim.assignments[clusterId];
  dim.assignments[clusterId] = {
    stateId,
    notes: notes ?? existing?.notes ?? '',
  };

  saveCustomModel(model);
}

/** Update notes for a cluster assignment */
export function updateNotes(
  model: CustomValidationModel,
  dimensionId: string,
  clusterId: string,
  notes: string,
): void {
  const dim = model.dimensions.find(d => d.id === dimensionId);
  if (!dim) return;

  if (dim.assignments[clusterId]) {
    dim.assignments[clusterId].notes = notes;
  } else {
    // Create assignment with first state as default
    dim.assignments[clusterId] = {
      stateId: dim.states[0]?.id ?? '',
      notes,
    };
  }

  saveCustomModel(model);
}

/** Get the next state in the cycle for a dimension */
export function cycleState(
  dimension: CustomDimension,
  currentStateId: string | undefined,
): CustomState | null {
  if (dimension.states.length === 0) return null;

  if (!currentStateId) {
    return dimension.states[0];
  }

  const currentIdx = dimension.states.findIndex(s => s.id === currentStateId);
  const nextIdx = (currentIdx + 1) % dimension.states.length;
  return dimension.states[nextIdx];
}

/** Get the current state object for a cluster assignment */
export function getAssignedState(
  dimension: CustomDimension,
  clusterId: string,
): CustomState | null {
  const assignment = dimension.assignments[clusterId];
  if (!assignment) return null;
  return dimension.states.find(s => s.id === assignment.stateId) ?? null;
}
